import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORIES, Category, Era, ERAS, HEADLINE_STATS, ISSUES } from './data';
import Timeline from './components/Timeline';
import CategoryBars from './components/CategoryBars';
import HeadlineCollage from './components/HeadlineCollage';
import TrumpPortrait from './components/TrumpPortrait';
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../../lib/gsap';
import { useReveal } from '../../Components/Fx/useReveal';
import { useCachedGet } from '../../Services/useCachedGet';
import EventCard from '../../Components/EventCard';

// Newest first, both for the period groups and for entries within them.
const ERA_ORDER: Era[] = ['second', 'between', 'first'];

const formatDate = (date: string) =>
   new Date(date.length === 7 ? `${date}-15` : date).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      ...(date.length > 7 ? { day: 'numeric' } : {}),
   });

const TrumpPage = () => {
   const page = useRef<HTMLDivElement>(null);
   const header = useRef<HTMLElement>(null);
   const [era, setEra] = useState<Era | 'all'>('all');
   const [category, setCategory] = useState<Category | null>(null);
   const [highlight, setHighlight] = useState<string | null>(null);
   const { data: events } = useCachedGet<any[]>(`${process.env.REACT_APP_API}/events`);

   const relatedCases = useMemo(
      () => (events || []).filter(e => e.tags?.some((t: any) => /trump/i.test(t?.label || ''))),
      [events]
   );

   const filtered = useMemo(
      () =>
         ISSUES.filter(i => (era === 'all' || i.era === era) && (!category || i.category === category)).sort((a, b) =>
            b.date.localeCompare(a.date)
         ),
      [era, category]
   );

   // Header: split title, count-up stats, rule draw
   useGSAP(
      () => {
         const counters = gsap.utils.toArray<HTMLElement>('[data-count]');
         if (prefersReducedMotion()) return;
         const split = SplitText.create('[data-trump-title]', {
            type: 'chars,lines',
            mask: 'lines',
            autoSplit: true,
            onSplit: self =>
               gsap.from(self.chars, { yPercent: 120, rotate: 6, stagger: 0.035, duration: 1.5, ease: 'expo.out' }),
         });
         gsap.from('[data-trump-fade]', { autoAlpha: 0, y: 20, stagger: 0.12, delay: 0.5 });
         gsap.from('[data-term-bar]', { scaleX: 0, transformOrigin: 'left', duration: 1.6, stagger: 0.25, delay: 0.4, ease: 'expo.inOut' });
         counters.forEach(el => {
            const target = Number(el.dataset.count);
            const obj = { v: 0 };
            gsap.to(obj, {
               v: target,
               duration: 2.2,
               delay: 0.8,
               ease: 'power3.out',
               onUpdate: () => {
                  el.textContent = Math.round(obj.v).toLocaleString('en-US');
               },
            });
         });
         return () => split.revert();
      },
      { scope: header }
   );

   useReveal(page, [filtered, relatedCases]);

   const jumpTo = (id: string) => {
      setEra('all');
      setCategory(null);
      setHighlight(id);
      requestAnimationFrame(() => {
         const el = document.getElementById(`issue-${id}`);
         if (!el) return;
         el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
         gsap.fromTo(el, { backgroundColor: 'rgba(255,42,61,0.18)' }, { backgroundColor: 'rgba(255,42,61,0)', duration: 2.4, delay: 0.4 });
      });
   };

   const grouped = ERA_ORDER.map(e => ({ era: e, items: filtered.filter(i => i.era === e) })).filter(g => g.items.length);

   return (
      <div ref={page}>
         {/* ── Header ─────────────────────────────────────────── */}
         <header ref={header} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden pb-14 pt-32">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_600px_at_20%_70%,rgba(255,42,61,0.12),transparent_70%)]" />
            <TrumpPortrait className="-right-[18%] h-[70svh] opacity-40 md:right-[-4%] md:h-[92svh] md:opacity-100 xl:right-[2%]" />
            <HeadlineCollage />

            <div className="page relative">
               <div data-trump-fade className="mb-8 flex items-center gap-3">
                  <span className="h-px w-8 bg-signal" />
                  <span className="eyebrow text-signal-soft">Special file · 45th & 47th president</span>
               </div>

               <h1
                  data-trump-title
                  className="font-display text-[19vw] leading-[0.82] tracking-tightest text-bone md:text-[11rem] xl:text-[13rem]"
               >
                  The Trump
                  <br />
                  <em className="text-signal">Years.</em>
               </h1>

               <div className="mt-10 grid max-w-3xl grid-cols-2 gap-6" data-trump-fade>
                  {(['first', 'second'] as Era[]).map(e => (
                     <div key={e}>
                        <p className="eyebrow mb-2">{ERAS[e].label}</p>
                        <p className="font-display text-4xl text-bone md:text-5xl">{ERAS[e].span}</p>
                        <span data-term-bar className="mt-3 block h-1 w-full rounded-full bg-signal" />
                     </div>
                  ))}
               </div>

               <p data-trump-fade className="mt-10 max-w-xl text-lg leading-relaxed text-bone-muted">
                  Corruption, abuses of power, immigration crackdowns and the courts. A sourced record of what happened
                  across two presidencies and the years in between.
               </p>

               <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-white/[0.08] pt-10 md:grid-cols-4">
                  {HEADLINE_STATS.map(stat => (
                     <button
                        key={stat.label}
                        type="button"
                        onClick={() => jumpTo(stat.ref)}
                        className="group text-left"
                        data-trump-fade
                     >
                        <span className="block font-display text-6xl leading-none text-bone transition-colors group-hover:text-signal md:text-7xl">
                           {stat.prefix}
                           <span data-count={stat.value}>{stat.value.toLocaleString('en-US')}</span>
                           {stat.suffix}
                        </span>
                        <span className="eyebrow mt-3 block transition-colors group-hover:text-bone">{stat.label} ↓</span>
                     </button>
                  ))}
               </div>
            </div>
         </header>

         {/* ── Graphics ───────────────────────────────────────── */}
         <section className="page mt-16">
            <div className="mb-8 flex items-end justify-between" data-reveal>
               <div>
                  <p className="eyebrow mb-3">Timeline</p>
                  <h2 className="headline mb-0">Ten years, {ISSUES.length} documented issues</h2>
               </div>
               <p className="hidden max-w-xs text-right text-sm text-bone-dim md:block">
                  Hover a mark for the headline. Click it to jump to the entry.
               </p>
            </div>
            <div className="card px-4 pb-4 pt-6 md:px-6" data-reveal>
               <Timeline onSelect={jumpTo} />
            </div>
         </section>

         <section className="page mt-20 grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4" data-reveal>
               <p className="eyebrow mb-3">By category</p>
               <h2 className="headline">Where the record lands</h2>
               <p className="text-bone-muted">
                  Click a category to filter the list. Hover a bar to see how it splits across the terms.
               </p>
            </div>
            <div className="card lg:col-span-8" data-reveal>
               <CategoryBars active={category} onToggle={c => setCategory(category === c ? null : c)} />
            </div>
         </section>

         {/* ── The list ───────────────────────────────────────── */}
         <section className="page mt-28">
            <div className="sticky top-16 z-20 -mx-5 mb-10 border-b border-white/[0.06] bg-ink-950/80 px-5 py-4 backdrop-blur-xl md:-mx-8 md:px-8">
               <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by period">
                     {(['all', ...ERA_ORDER] as const).map(e => (
                        <button
                           key={e}
                           type="button"
                           role="tab"
                           aria-selected={era === e}
                           onClick={() => setEra(e)}
                           className={era === e ? 'chip-signal' : 'chip hover:text-bone'}
                        >
                           {e === 'all' ? 'All years' : `${ERAS[e].label} · ${ERAS[e].span}`}
                        </button>
                     ))}
                  </div>
                  <div className="flex items-center gap-3">
                     {category && (
                        <button type="button" onClick={() => setCategory(null)} className="chip-signal">
                           {CATEGORIES[category]} ✕
                        </button>
                     )}
                     <span className="font-mono text-xs text-bone-dim">{filtered.length} entries</span>
                  </div>
               </div>
            </div>

            {grouped.map(group => (
               <div key={group.era} className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
                  <div className="lg:col-span-3">
                     <div className="lg:sticky lg:top-40" data-reveal>
                        <p className="eyebrow mb-2 text-signal-soft">{ERAS[group.era].label}</p>
                        <p className="font-display text-5xl text-bone">{ERAS[group.era].span}</p>
                        <p className="mt-2 font-mono text-xs text-bone-dim">{group.items.length} entries</p>
                     </div>
                  </div>
                  <ol className="border-t border-white/[0.07] lg:col-span-9">
                     {group.items.map(issue => (
                        <li
                           key={issue.id}
                           id={`issue-${issue.id}`}
                           data-reveal
                           className={`grid scroll-mt-40 grid-cols-1 gap-3 border-b border-white/[0.07] px-2 py-8 md:grid-cols-[7rem_1fr] md:gap-8 ${
                              highlight === issue.id ? 'relative' : ''
                           }`}
                        >
                           <div className="font-mono text-xs uppercase tracking-[0.14em] text-bone-dim">
                              {formatDate(issue.date)}
                           </div>
                           <div>
                              <div className="mb-3 flex flex-wrap items-center gap-2">
                                 <button
                                    type="button"
                                    onClick={() => setCategory(issue.category)}
                                    className="chip hover:border-signal/50 hover:text-bone"
                                 >
                                    {CATEGORIES[issue.category]}
                                 </button>
                                 {issue.figure && <span className="chip-signal">{issue.figure}</span>}
                              </div>
                              <h3 className="font-display text-3xl leading-tight text-bone md:text-4xl">{issue.title}</h3>
                              <p className="mt-3 max-w-3xl leading-relaxed text-bone-muted">{issue.summary}</p>
                              <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1">
                                 {issue.sources.map(s => (
                                    <li key={s.url}>
                                       <a
                                          href={s.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="underline-grow font-mono text-[11px] uppercase tracking-[0.14em] text-bone-dim hover:text-bone"
                                       >
                                          {s.label} ↗
                                       </a>
                                    </li>
                                 ))}
                              </ul>
                           </div>
                        </li>
                     ))}
                  </ol>
               </div>
            ))}

            {!filtered.length && <p className="py-20 text-center text-bone-muted">No entries match these filters.</p>}

            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-bone-dim" data-reveal>
               This file is not exhaustive. Entries summarise court rulings, official actions and verified reporting,
               and note acquittals, dismissals and reversals where they occurred. Last reviewed November 3, 2026.{' '}
               <Link to="/submit" className="text-bone underline underline-offset-4 hover:text-signal">
                  Suggest an addition
               </Link>
               .
            </p>
         </section>

         {relatedCases.length > 0 && (
            <section className="page mt-28">
               <div className="mb-10" data-reveal>
                  <p className="eyebrow mb-3">From the archive</p>
                  <h2 className="headline mb-0">Related case files</h2>
               </div>
               <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {relatedCases.map((event, i) => (
                     <Link to={`/event/${event.id}`} key={event.id} className="block" data-reveal>
                        <EventCard event={event} index={i} />
                     </Link>
                  ))}
               </div>
            </section>
         )}
      </div>
   );
};

export default TrumpPage;
